import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Checkbox, Divider, useToast, Flex, InputGroup, InputLeftElement, Icon, List, ListItem,
  Spinner, Badge, Textarea, Wrap, Tag, TagLabel, TagCloseButton, Select, IconButton, Switch,
} from '@chakra-ui/react';
import { FiSearch, FiEdit2, FiArrowLeft, FiPlus, FiTrash2, FiBriefcase, FiX } from 'react-icons/fi';
import api from '../api';
import ImageUpload from './ImageUpload';
import SocialLinksEditor from './SocialLinksEditor';

// Inline company search for editing a contact's company
function CompanyPicker({ value, companyId, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  const search = async (q) => {
    if (q.length < 1) { setResults([]); return; }
    try {
      const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`);
      setResults(data); setOpen(true);
    } catch (e) {}
  };

  return (
    <Box position="relative">
      <Input size="sm" value={query} placeholder={value || 'Search company...'}
        onChange={(e) => { setQuery(e.target.value); clearTimeout(debounce.current); debounce.current = setTimeout(() => search(e.target.value), 200); }}
        onFocus={() => query.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && results.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
          borderRadius="md" boxShadow="lg" zIndex={200} maxH="150px" overflowY="auto">
          <List spacing={0}>
            {results.map(c => (
              <ListItem key={c.id} px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                onClick={() => { onChange(c.id, c.name); setQuery(''); setOpen(false); }}>
                {c.name}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}

function AddAsUserButton({ contact }) {
  const [loading, setLoading] = React.useState(false);
  const [existingUser, setExistingUser] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ username: '', password: '', role: 'user' });
  const toast = useToast();

  React.useEffect(() => {
    if (contact?.email) {
      api.get('/users').then(r => {
        const match = r.data.find(u => u.email === contact.email);
        setExistingUser(match || null);
      }).catch(() => {});
    }
  }, [contact]);

  const createUser = async () => {
    if (!form.username) { toast({ title: 'Username required', status: 'warning', duration: 2000 }); return; }
    setLoading(true);
    try {
      await api.post('/users', {
        username: form.username,
        display_name: `${contact.first_name} ${contact.last_name || ''}`.trim(),
        email: contact.email || '',
        password: form.password || Math.random().toString(36).slice(-10) + 'Aa1!',
        role: form.role,
      });
      toast({ title: 'User account created', description: `${contact.first_name} can now log in`, status: 'success', duration: 3000 });
      setShowForm(false);
      setExistingUser({ email: contact.email });
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Failed to create user', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  if (existingUser) {
    return (
      <Badge colorScheme="green" fontSize="9px" px={2} py={1} borderRadius="md">
        ✓ Has User Account
      </Badge>
    );
  }

  if (showForm) {
    return (
      <Box bg="blue.50" p={2} borderRadius="md" border="1px solid" borderColor="blue.200">
        <Text fontSize="10px" fontWeight="700" color="blue.700" mb={2}>Create User Account for {contact.first_name}</Text>
        <VStack spacing={2}>
          <HStack w="100%">
            <FormControl isRequired>
              <FormLabel fontSize="9px" mb={0}>Username</FormLabel>
              <Input size="xs" value={form.username}
                onChange={e => setForm(p => ({...p, username: e.target.value}))}
                placeholder={contact.email?.split('@')[0] || contact.first_name?.toLowerCase()} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="9px" mb={0}>Role</FormLabel>
              <Select size="xs" value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Select>
            </FormControl>
          </HStack>
          <FormControl>
            <FormLabel fontSize="9px" mb={0}>Temporary Password (leave blank to auto-generate)</FormLabel>
            <Input size="xs" type="password" value={form.password}
              onChange={e => setForm(p => ({...p, password: e.target.value}))}
              placeholder="Auto-generate if blank" />
          </FormControl>
          <HStack w="100%" justify="flex-end">
            <Button size="xs" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="xs" colorScheme="blue" onClick={createUser} isLoading={loading}>Create Account</Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Button size="xs" colorScheme="blue" variant="outline"
      onClick={() => {
        setForm({ username: contact.email?.split('@')[0] || contact.first_name?.toLowerCase() || '', password: '', role: 'user' });
        setShowForm(true);
      }}>
      + Add as User
    </Button>
  );
}

const COMM_COLORS = {
  phone: 'teal', mobile: 'teal', landline: 'gray', email: 'blue',
  website: 'purple', linkedin: 'blue', twitter: 'cyan', other: 'gray',
};

const ADDR_TYPES = ['work','home','billing','shipping','other'];
const COMM_TYPES = ['phone','mobile','landline','email','website','linkedin','twitter','other'];

function AddressForm({ form, setForm, onSave, onCancel }) {
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Box p={3} border="1px" borderColor="teal.200" borderRadius="md" bg="teal.50" mb={2}>
      <VStack spacing={2} align="stretch">
        <HStack spacing={2}>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
            <Select size="sm" bg="white" value={form.address_type} onChange={e => set('address_type', e.target.value)}>
              {ADDR_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </Select>
          </FormControl>
          <FormControl flex={2}>
            <FormLabel fontSize="xs" mb={0.5}>Label (optional)</FormLabel>
            <Input size="sm" bg="white" placeholder="e.g. Head Office" value={form.label} onChange={e => set('label', e.target.value)} />
          </FormControl>
        </HStack>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Address Line 1</FormLabel>
          <Input size="sm" bg="white" value={form.line1} onChange={e => set('line1', e.target.value)} autoFocus />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Address Line 2</FormLabel>
          <Input size="sm" bg="white" value={form.line2} onChange={e => set('line2', e.target.value)} />
        </FormControl>
        <HStack spacing={2}>
          <FormControl flex={2}>
            <FormLabel fontSize="xs" mb={0.5}>City / Town</FormLabel>
            <Input size="sm" bg="white" value={form.city} onChange={e => set('city', e.target.value)} />
          </FormControl>
          <FormControl flex={2}>
            <FormLabel fontSize="xs" mb={0.5}>County / State</FormLabel>
            <Input size="sm" bg="white" value={form.county} onChange={e => set('county', e.target.value)} />
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={0.5}>Postcode</FormLabel>
            <Input size="sm" bg="white" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
          </FormControl>
        </HStack>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Country</FormLabel>
          <Input size="sm" bg="white" value={form.country} onChange={e => set('country', e.target.value)} placeholder="e.g. United Kingdom" />
        </FormControl>
        <HStack>
          <Checkbox size="sm" isChecked={form.is_primary} onChange={e => set('is_primary', e.target.checked)} colorScheme="teal">
            <Text fontSize="11px">Set as primary address</Text>
          </Checkbox>
        </HStack>
        <HStack justify="flex-end" spacing={2}>
          <Button size="xs" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button size="xs" colorScheme="teal" isLoading={saving} onClick={async () => { setSaving(true); await onSave(); setSaving(false); }}>Save Address</Button>
        </HStack>
      </VStack>
    </Box>
  );
}

function CommForm({ form, setForm, onSave, onCancel }) {
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const placeholder = { phone: '+44 20 7123 4567', mobile: '+44 7700 900000', landline: '+44 20 7000 0000', email: 'name@company.com', website: 'https://www.company.com', linkedin: 'linkedin.com/in/name', twitter: '@handle', other: '' };
  return (
    <Box p={3} border="1px" borderColor="teal.200" borderRadius="md" bg="teal.50" mb={2}>
      <VStack spacing={2} align="stretch">
        <HStack spacing={2}>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
            <Select size="sm" bg="white" value={form.comm_type} onChange={e => set('comm_type', e.target.value)}>
              {COMM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </Select>
          </FormControl>
          <FormControl flex={2}>
            <FormLabel fontSize="xs" mb={0.5}>Label (optional)</FormLabel>
            <Input size="sm" bg="white" placeholder="e.g. Work Direct" value={form.label} onChange={e => set('label', e.target.value)} />
          </FormControl>
        </HStack>
        <FormControl isRequired>
          <FormLabel fontSize="xs" mb={0.5}>Value *</FormLabel>
          <Input size="sm" bg="white" value={form.value} onChange={e => set('value', e.target.value)}
            placeholder={placeholder[form.comm_type] || ''} autoFocus
            type={form.comm_type === 'email' ? 'email' : 'text'} />
        </FormControl>
        <HStack>
          <Checkbox size="sm" isChecked={form.is_primary} onChange={e => set('is_primary', e.target.checked)} colorScheme="teal">
            <Text fontSize="11px">Set as primary for this type</Text>
          </Checkbox>
        </HStack>
        <HStack justify="flex-end" spacing={2}>
          <Button size="xs" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button size="xs" colorScheme="teal" isLoading={saving}
            onClick={async () => { if (!form.value.trim()) return; setSaving(true); await onSave(); setSaving(false); }}>Save</Button>
        </HStack>
      </VStack>
    </Box>
  );
}

function ContactBankAccounts({ contactId }) {
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' });
  const [editId, setEditId] = React.useState(null);
  const toast = useToast();

  const fetch = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/bank-accounts?contact_id=${contactId}`);
      setAccounts(data.filter(a => !a.deleted_at));
    } catch(e) {}
    setLoading(false);
  }, [contactId]);

  React.useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/bank-accounts/${editId}`, form);
      } else {
        await api.post('/bank-accounts', { ...form, contact_id: contactId, is_own: false });
      }
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      setShowForm(false); setEditId(null); setForm({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' });
      fetch();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const softDelete = async (id) => {
    await api.delete(`/bank-accounts/${id}`);
    fetch();
  };

  const startEdit = (a) => { setForm({ bank_name: a.bank_name, sort_code: a.sort_code||'', account_number: a.account_number||'', account_holder: a.account_holder||'', iban: a.iban||'', swift_bic: a.swift_bic||'' }); setEditId(a.id); setShowForm(true); };

  return (
    <Box mt={2}>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">Bank Accounts</Text>
        <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiPlus size={10} />} onClick={() => { setShowForm(p => !p); setEditId(null); setForm({ bank_name: '', sort_code: '', account_number: '', account_holder: '', iban: '', swift_bic: '' }); }}>Add</Button>
      </Flex>
      {loading ? <Spinner size="xs" /> : accounts.length === 0 && !showForm ? <Text fontSize="10px" color="gray.400">No bank accounts</Text> : null}
      {accounts.map(a => (
        <Box key={a.id} p={2} bg="gray.50" borderRadius="md" mb={1} border="1px solid" borderColor="gray.200">
          <Flex justify="space-between">
            <Box>
              <Text fontSize="11px" fontWeight="600">{a.bank_name}</Text>
              <Text fontSize="10px" color="gray.600">{a.sort_code && `${a.sort_code} / `}{a.account_number}</Text>
              {a.account_holder && <Text fontSize="10px" color="gray.500">{a.account_holder}</Text>}
              {a.iban && <Text fontSize="9px" color="gray.400">IBAN: {a.iban}</Text>}
            </Box>
            <HStack spacing={1}>
              <IconButton icon={<FiEdit2 size={10} />} size="xs" variant="ghost" onClick={() => startEdit(a)} aria-label="Edit" />
              <IconButton icon={<FiTrash2 size={10} />} size="xs" variant="ghost" colorScheme="red" onClick={() => softDelete(a.id)} aria-label="Delete" />
            </HStack>
          </Flex>
        </Box>
      ))}
      {showForm && (
        <Box p={2} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
          <VStack spacing={2}>
            <HStack w="100%">
              <FormControl isRequired><FormLabel fontSize="9px" mb={0}>Bank Name</FormLabel><Input size="xs" value={form.bank_name} onChange={e => setForm(p => ({...p, bank_name: e.target.value}))} /></FormControl>
              <FormControl><FormLabel fontSize="9px" mb={0}>Account Holder</FormLabel><Input size="xs" value={form.account_holder} onChange={e => setForm(p => ({...p, account_holder: e.target.value}))} /></FormControl>
            </HStack>
            <HStack w="100%">
              <FormControl><FormLabel fontSize="9px" mb={0}>Sort Code</FormLabel><Input size="xs" value={form.sort_code} onChange={e => setForm(p => ({...p, sort_code: e.target.value}))} placeholder="00-00-00" /></FormControl>
              <FormControl><FormLabel fontSize="9px" mb={0}>Account Number</FormLabel><Input size="xs" value={form.account_number} onChange={e => setForm(p => ({...p, account_number: e.target.value}))} /></FormControl>
            </HStack>
            <HStack w="100%">
              <FormControl><FormLabel fontSize="9px" mb={0}>IBAN</FormLabel><Input size="xs" value={form.iban} onChange={e => setForm(p => ({...p, iban: e.target.value}))} /></FormControl>
              <FormControl><FormLabel fontSize="9px" mb={0}>SWIFT/BIC</FormLabel><Input size="xs" value={form.swift_bic} onChange={e => setForm(p => ({...p, swift_bic: e.target.value}))} /></FormControl>
            </HStack>
            <HStack w="100%" justify="flex-end">
              <Button size="xs" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
              <Button size="xs" colorScheme="blue" onClick={save}>{editId ? 'Update' : 'Add'}</Button>
            </HStack>
          </VStack>
        </Box>
      )}
    </Box>
  );
}

export default function ContactsListModal({ isOpen, onClose, initialContactId, initialEdit }) {
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  // Add contact state
  const [addingContact, setAddingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'', company_id:'', company_name:'' });
  const [savingContact, setSavingContact] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  // Addresses & comms
  const [addresses, setAddresses] = useState([]);
  const [comms, setComms] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null); // null | 'new' | {id,...}
  const [editingComm, setEditingComm] = useState(null);      // null | 'new' | {id,...}
  const [addrForm, setAddrForm] = useState({ address_type:'work', label:'', line1:'', line2:'', city:'', county:'', postcode:'', country:'', is_primary: false });
  const [commForm, setCommForm] = useState({ comm_type:'phone', label:'', value:'', is_primary: false });

  const fetch = useCallback(async () => {
    try {
      const { data } = query.length > 0
        ? await api.get(`/contacts/search?q=${encodeURIComponent(query)}`)
        : await api.get('/contacts');
      setContacts(data);
    } catch (e) {}
  }, [query]);

  useEffect(() => {
    if (isOpen) { setSelected(null); setEditing(false); setQuery(''); fetch(); }
  }, [isOpen]);

  // Auto-open a specific contact when launched from another modal (e.g.
  // CompaniesListModal, or the contact quick-view's pencil icon). When
  // initialEdit is true, the contact is opened directly in edit mode —
  // this is what the pencil icon on ContactQuickViewModal uses, so the
  // user doesn't have to click Edit a second time.
  useEffect(() => {
    if (isOpen && initialContactId) {
      (async () => {
        await openView({ id: initialContactId });
        if (initialEdit) {
          // Re-fetch the full contact and slide into edit mode.
          try {
            const { data } = await api.get(`/contacts/${initialContactId}`);
            setForm({ ...data });
            setEditing(true);
          } catch (e) {}
        }
      })();
    }
  }, [isOpen, initialContactId, initialEdit]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(fetch, 200);
    return () => clearTimeout(t);
  }, [query, fetch]);

  const openEdit = (contact) => {
    setSelected(contact);
    api.get(`/projects?contact_id=${contact.id}&limit=50`).then(r => setLinkedProjects(r.data || [])).catch(() => setLinkedProjects([]));
    // Load addresses and comms
    api.get(`/contacts/${contact.id}/addresses`).then(r => setAddresses(r.data || [])).catch(() => setAddresses([]));
    api.get(`/contacts/${contact.id}/comms`).then(r => setComms(r.data || [])).catch(() => setComms([]));
    setForm({ ...contact });
    setEditing(true);
    setEditingAddress(null); setEditingComm(null);
  };

  const openView = async (contact) => {
    try {
      const { data } = await api.get(`/contacts/${contact.id}`);
      setSelected(data);
      setEditing(false);
      setEditingAddress(null); setEditingComm(null);
      // Fetch addresses and comms in parallel
      const [addrRes, commsRes] = await Promise.allSettled([
        api.get(`/contacts/${contact.id}/addresses`),
        api.get(`/contacts/${contact.id}/comms`),
      ]);
      setAddresses(addrRes.status === 'fulfilled' ? addrRes.value.data || [] : []);
      setComms(commsRes.status === 'fulfilled' ? commsRes.value.data || [] : []);
    } catch (e) {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/contacts/${selected.id}`, form);
      toast({ title: 'Contact updated', status: 'success', duration: 1500 });
      setEditing(false);
      await openView({ id: selected.id });
      fetch();
    } catch (e) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleChange = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const searchCompanies = async (q) => {
    if (q.length < 2) { setCompanySuggestions([]); return; }
    try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`); setCompanySuggestions(data || []); } catch(e) {}
  };

  const saveNewContact = async () => {
    if (!contactForm.first_name.trim()) { toast({ title: 'First name required', status: 'warning', duration: 1500 }); return; }
    setSavingContact(true);
    try {
      await api.post('/contacts', { ...contactForm, company_id: contactForm.company_id || null });
      toast({ title: 'Contact added', status: 'success', duration: 1500 });
      setAddingContact(false);
      setContactForm({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'', company_id:'', company_name:'' });
      setCompanySearch(''); setCompanySuggestions([]);
      fetch();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSavingContact(false);
  };

  const goBack = () => { setSelected(null); setEditing(false); setLinkedProjects([]); setAddresses([]); setComms([]); setEditingAddress(null); setEditingComm(null); };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}>
            {selected && <Icon as={FiArrowLeft} cursor="pointer" onClick={goBack} />}
            Contacts
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!selected && (
            <>
              <InputGroup mb={3}>
                <InputLeftElement h="32px"><Icon as={FiSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                <Input size="sm" pl={8} placeholder="Search contacts..." value={query}
                  onChange={(e) => setQuery(e.target.value)} />
              </InputGroup>
              <VStack spacing={1} align="stretch">
                {contacts.map(ct => (
                  <Box key={ct.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                    cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => openView(ct)}>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text fontSize="12px" fontWeight="600">{ct.first_name} {ct.last_name || ''}</Text>
                        {ct.company_name && <Text fontSize="10px" color="gray.500">{ct.company_name}</Text>}
                        {ct.email && <Text fontSize="10px" color="gray.400">{ct.email}</Text>}
                      </Box>
                      <Icon as={FiEdit2} color="gray.400" boxSize={3}
                        onClick={(e) => { e.stopPropagation(); openEdit(ct); }} />
                    </Flex>
                  </Box>
                ))}
                {contacts.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No contacts found</Text>
                )}
              </VStack>
            </>
          )}

          {selected && !editing && (
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="600">{selected.first_name} {selected.last_name || ''}</Text>
                <HStack>
                  <AddAsUserButton contact={selected} />
                  <Button size="xs" leftIcon={<FiEdit2 />} onClick={() => { setForm({ ...selected }); setEditing(true); }}>
                    Edit
                  </Button>
                </HStack>
              </Flex>
              {[
                ['Company', selected.company_name],
                ['Job Title', selected.job_title],
                ['Email', selected.email],
                ['Mobile', selected.mobile],
                ['Landline', selected.landline],
                ['Birthday', selected.birthday ? new Date(selected.birthday.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null],
                ['Notes', selected.description],
              ].filter(([,v]) => v).map(([label, val]) => (
                <Box key={label}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">{label}</Text>
                  <Text fontSize="12px">{val}</Text>
                </Box>
              ))}
              <SocialLinksEditor entityType="contact" entityId={selected.id} />
              <ContactBankAccounts contactId={selected.id} />

              {/* Addresses */}
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">📍 Addresses</Text>
                  <Button size="xs" leftIcon={<FiPlus />} variant="ghost" colorScheme="teal"
                    onClick={() => { setAddrForm({ address_type:'work', label:'', line1:'', line2:'', city:'', county:'', postcode:'', country:'', is_primary: false }); setEditingAddress('new'); }}>
                    Add
                  </Button>
                </Flex>
                {editingAddress === 'new' && (
                  <AddressForm form={addrForm} setForm={setAddrForm}
                    onSave={async () => { await api.post(`/contacts/${selected.id}/addresses`, addrForm); const r = await api.get(`/contacts/${selected.id}/addresses`); setAddresses(r.data); setEditingAddress(null); }}
                    onCancel={() => setEditingAddress(null)} />
                )}
                <VStack spacing={1} align="stretch">
                  {addresses.map(addr => (
                    <Box key={addr.id}>
                      {editingAddress?.id === addr.id ? (
                        <AddressForm form={addrForm} setForm={setAddrForm}
                          onSave={async () => { await api.put(`/contacts/${selected.id}/addresses/${addr.id}`, addrForm); const r = await api.get(`/contacts/${selected.id}/addresses`); setAddresses(r.data); setEditingAddress(null); }}
                          onCancel={() => setEditingAddress(null)} />
                      ) : (
                        <Flex p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="flex-start" bg="gray.50">
                          <Box>
                            <HStack spacing={1} mb={0.5}>
                              <Badge fontSize="8px" colorScheme="blue" textTransform="capitalize">{addr.address_type}</Badge>
                              {addr.label && <Text fontSize="10px" color="gray.500">{addr.label}</Text>}
                              {addr.is_primary ? <Badge fontSize="8px" colorScheme="green">Primary</Badge> : null}
                            </HStack>
                            <Text fontSize="11px">{[addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country].filter(Boolean).join(', ')}</Text>
                          </Box>
                          <HStack spacing={0}>
                            <IconButton size="xs" icon={<FiEdit2 size={10} />} variant="ghost" aria-label="Edit"
                              onClick={() => { setAddrForm({ address_type: addr.address_type||'work', label: addr.label||'', line1: addr.line1||'', line2: addr.line2||'', city: addr.city||'', county: addr.county||'', postcode: addr.postcode||'', country: addr.country||'', is_primary: !!addr.is_primary }); setEditingAddress(addr); }} />
                            <IconButton size="xs" icon={<FiTrash2 size={10} />} variant="ghost" colorScheme="red" aria-label="Delete"
                              onClick={async () => { await api.delete(`/contacts/${selected.id}/addresses/${addr.id}`); setAddresses(a => a.filter(x => x.id !== addr.id)); }} />
                          </HStack>
                        </Flex>
                      )}
                    </Box>
                  ))}
                  {addresses.length === 0 && !editingAddress && <Text fontSize="11px" color="gray.400" fontStyle="italic">No addresses</Text>}
                </VStack>
              </Box>

              {/* Additional comms */}
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">📞 Additional Contact Details</Text>
                  <Button size="xs" leftIcon={<FiPlus />} variant="ghost" colorScheme="teal"
                    onClick={() => { setCommForm({ comm_type:'phone', label:'', value:'', is_primary: false }); setEditingComm('new'); }}>
                    Add
                  </Button>
                </Flex>
                {editingComm === 'new' && (
                  <CommForm form={commForm} setForm={setCommForm}
                    onSave={async () => { await api.post(`/contacts/${selected.id}/comms`, commForm); const r = await api.get(`/contacts/${selected.id}/comms`); setComms(r.data); setEditingComm(null); }}
                    onCancel={() => setEditingComm(null)} />
                )}
                <VStack spacing={1} align="stretch">
                  {comms.map(comm => (
                    <Box key={comm.id}>
                      {editingComm?.id === comm.id ? (
                        <CommForm form={commForm} setForm={setCommForm}
                          onSave={async () => { await api.put(`/contacts/${selected.id}/comms/${comm.id}`, commForm); const r = await api.get(`/contacts/${selected.id}/comms`); setComms(r.data); setEditingComm(null); }}
                          onCancel={() => setEditingComm(null)} />
                      ) : (
                        <Flex p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="center" bg="gray.50">
                          <Box>
                            <HStack spacing={1}>
                              <Badge fontSize="8px" colorScheme={COMM_COLORS[comm.comm_type]||'gray'} textTransform="capitalize">{comm.comm_type}</Badge>
                              {comm.label && <Text fontSize="10px" color="gray.500">{comm.label}</Text>}
                              {comm.is_primary ? <Badge fontSize="8px" colorScheme="green">Primary</Badge> : null}
                            </HStack>
                            {comm.comm_type === 'website' || comm.comm_type === 'linkedin' || comm.comm_type === 'twitter'
                              ? <Text fontSize="11px" color="blue.500" cursor="pointer" onClick={() => window.open(comm.value.startsWith('http') ? comm.value : `https://${comm.value}`, '_blank')}>{comm.value}</Text>
                              : <Text fontSize="11px">{comm.value}</Text>}
                          </Box>
                          <HStack spacing={0}>
                            <IconButton size="xs" icon={<FiEdit2 size={10} />} variant="ghost" aria-label="Edit"
                              onClick={() => { setCommForm({ comm_type: comm.comm_type, label: comm.label||'', value: comm.value, is_primary: !!comm.is_primary }); setEditingComm(comm); }} />
                            <IconButton size="xs" icon={<FiTrash2 size={10} />} variant="ghost" colorScheme="red" aria-label="Delete"
                              onClick={async () => { await api.delete(`/contacts/${selected.id}/comms/${comm.id}`); setComms(c => c.filter(x => x.id !== comm.id)); }} />
                          </HStack>
                        </Flex>
                      )}
                    </Box>
                  ))}
                  {comms.length === 0 && !editingComm && <Text fontSize="11px" color="gray.400" fontStyle="italic">No additional contact details</Text>}
                </VStack>
              </Box>
              {linkedProjects.length > 0 && (
                <Box>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase" mb={2}>
                    <FiBriefcase size={10} style={{ display:'inline', marginRight:4 }} />
                    Linked Projects &amp; Companies ({linkedProjects.length})
                  </Text>
                  <VStack spacing={1} align="stretch">
                    {linkedProjects.map(p => (
                      <Box key={p.id} p={2} border="1px" borderColor="gray.100" borderRadius="md" bg="gray.50">
                        <Text fontSize="12px" fontWeight="600" color="gray.800">{p.project_name}</Text>
                        <HStack spacing={2} mt={0.5} flexWrap="wrap">
                          {p.company_name && <Text fontSize="10px" color="purple.600" fontWeight="600">{p.company_name}</Text>}
                          {p.row_group_name && <Text fontSize="10px" color="gray.400">{p.row_group_name}</Text>}
                          {!!p.is_done && <Text fontSize="9px" color="green.500">✓ Done</Text>}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}

          {selected && editing && (
            <VStack spacing={3}>
              <Flex w="100%" justify="center">
                <ImageUpload
                  folder="avatars" entityType="contact" entityId={selected.id}
                  currentFileId={form.avatar_file_id || null}
                  onUpdate={(fileId) => { setForm(p => ({ ...p, avatar_file_id: fileId })); }}
                  size="80px" label="Avatar"
                />
              </Flex>
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
                <CompanyPicker
                  value={form.company_name || ''}
                  companyId={form.company_id}
                  onChange={(id, name) => setForm(p => ({ ...p, company_id: id, company_name: name }))}
                />
                {form.company_name && <Text fontSize="10px" color="teal.600" mt={0.5}>{form.company_name}</Text>}
              </FormControl>
              {[
                ['first_name', 'First Name *'],
                ['last_name', 'Last Name'],
                ['email', 'Email'],
                ['mobile', 'Mobile'],
                ['landline', 'Landline'],
                ['job_title', 'Job Title'],
              ].map(([field, label]) => (
                <FormControl key={field}>
                  <FormLabel fontSize="xs" mb={0.5}>{label}</FormLabel>
                  <Input size="sm" value={form[field] || ''} onChange={handleChange(field)} />
                </FormControl>
              ))}
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Notes / Description</FormLabel>
                <Textarea size="sm" rows={3} value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Any notes about this contact..." />
              </FormControl>
              <Divider />
              <FormControl>
                <FormLabel fontSize="xs" mb={0.5}>Birthday</FormLabel>
                <Input size="sm" type="date" value={form.birthday ? form.birthday.split('T')[0] : ''}
                  onChange={handleChange('birthday')} />
              </FormControl>
              <HStack spacing={4}>
                <Checkbox size="sm" isChecked={!!form.show_birthday_schedule}
                  onChange={(e) => setForm(p => ({ ...p, show_birthday_schedule: e.target.checked }))}>
                  <Text fontSize="11px">Show birthday in schedule</Text>
                </Checkbox>
                <Checkbox size="sm" isChecked={!!form.send_birthday_wish}
                  onChange={(e) => setForm(p => ({ ...p, send_birthday_wish: e.target.checked }))}>
                  <Text fontSize="11px">Wish this contact a happy birthday</Text>
                </Checkbox>
              </HStack>
              {selected && <SocialLinksEditor entityType="contact" entityId={selected.id} />}

              {/* Addresses in edit mode */}
              <Divider />
              <Box w="100%">
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">📍 Addresses</Text>
                  <Button size="xs" leftIcon={<FiPlus />} variant="ghost" colorScheme="teal"
                    onClick={() => { setAddrForm({ address_type:'work', label:'', line1:'', line2:'', city:'', county:'', postcode:'', country:'', is_primary: false }); setEditingAddress('new'); }}>
                    Add
                  </Button>
                </Flex>
                {editingAddress === 'new' && (
                  <AddressForm form={addrForm} setForm={setAddrForm}
                    onSave={async () => { await api.post(`/contacts/${selected.id}/addresses`, addrForm); const r = await api.get(`/contacts/${selected.id}/addresses`); setAddresses(r.data); setEditingAddress(null); }}
                    onCancel={() => setEditingAddress(null)} />
                )}
                <VStack spacing={1} align="stretch">
                  {addresses.map(addr => (
                    <Box key={addr.id}>
                      {editingAddress?.id === addr.id ? (
                        <AddressForm form={addrForm} setForm={setAddrForm}
                          onSave={async () => { await api.put(`/contacts/${selected.id}/addresses/${addr.id}`, addrForm); const r = await api.get(`/contacts/${selected.id}/addresses`); setAddresses(r.data); setEditingAddress(null); }}
                          onCancel={() => setEditingAddress(null)} />
                      ) : (
                        <Flex p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="flex-start" bg="gray.50">
                          <Box>
                            <HStack spacing={1} mb={0.5}>
                              <Badge fontSize="8px" colorScheme="blue" textTransform="capitalize">{addr.address_type}</Badge>
                              {addr.label && <Text fontSize="10px" color="gray.500">{addr.label}</Text>}
                              {addr.is_primary ? <Badge fontSize="8px" colorScheme="green">Primary</Badge> : null}
                            </HStack>
                            <Text fontSize="11px">{[addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country].filter(Boolean).join(', ')}</Text>
                          </Box>
                          <HStack spacing={0}>
                            <IconButton size="xs" icon={<FiEdit2 size={10} />} variant="ghost" aria-label="Edit"
                              onClick={() => { setAddrForm({ address_type: addr.address_type||'work', label: addr.label||'', line1: addr.line1||'', line2: addr.line2||'', city: addr.city||'', county: addr.county||'', postcode: addr.postcode||'', country: addr.country||'', is_primary: !!addr.is_primary }); setEditingAddress(addr); }} />
                            <IconButton size="xs" icon={<FiTrash2 size={10} />} variant="ghost" colorScheme="red" aria-label="Delete"
                              onClick={async () => { await api.delete(`/contacts/${selected.id}/addresses/${addr.id}`); setAddresses(a => a.filter(x => x.id !== addr.id)); }} />
                          </HStack>
                        </Flex>
                      )}
                    </Box>
                  ))}
                  {addresses.length === 0 && !editingAddress && <Text fontSize="11px" color="gray.400" fontStyle="italic">No addresses yet</Text>}
                </VStack>
              </Box>

              {/* Additional comms in edit mode */}
              <Box w="100%">
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">📞 Additional Contact Details</Text>
                  <Button size="xs" leftIcon={<FiPlus />} variant="ghost" colorScheme="teal"
                    onClick={() => { setCommForm({ comm_type:'phone', label:'', value:'', is_primary: false }); setEditingComm('new'); }}>
                    Add
                  </Button>
                </Flex>
                {editingComm === 'new' && (
                  <CommForm form={commForm} setForm={setCommForm}
                    onSave={async () => { await api.post(`/contacts/${selected.id}/comms`, commForm); const r = await api.get(`/contacts/${selected.id}/comms`); setComms(r.data); setEditingComm(null); }}
                    onCancel={() => setEditingComm(null)} />
                )}
                <VStack spacing={1} align="stretch">
                  {comms.map(comm => (
                    <Box key={comm.id}>
                      {editingComm?.id === comm.id ? (
                        <CommForm form={commForm} setForm={setCommForm}
                          onSave={async () => { await api.put(`/contacts/${selected.id}/comms/${comm.id}`, commForm); const r = await api.get(`/contacts/${selected.id}/comms`); setComms(r.data); setEditingComm(null); }}
                          onCancel={() => setEditingComm(null)} />
                      ) : (
                        <Flex p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="center" bg="gray.50">
                          <Box>
                            <HStack spacing={1}>
                              <Badge fontSize="8px" colorScheme={COMM_COLORS[comm.comm_type]||'gray'} textTransform="capitalize">{comm.comm_type}</Badge>
                              {comm.label && <Text fontSize="10px" color="gray.500">{comm.label}</Text>}
                              {comm.is_primary ? <Badge fontSize="8px" colorScheme="green">Primary</Badge> : null}
                            </HStack>
                            {comm.comm_type === 'website' || comm.comm_type === 'linkedin' || comm.comm_type === 'twitter'
                              ? <Text fontSize="11px" color="blue.500" cursor="pointer" onClick={() => window.open(comm.value.startsWith('http') ? comm.value : `https://${comm.value}`, '_blank')}>{comm.value}</Text>
                              : <Text fontSize="11px">{comm.value}</Text>}
                          </Box>
                          <HStack spacing={0}>
                            <IconButton size="xs" icon={<FiEdit2 size={10} />} variant="ghost" aria-label="Edit"
                              onClick={() => { setCommForm({ comm_type: comm.comm_type, label: comm.label||'', value: comm.value, is_primary: !!comm.is_primary }); setEditingComm(comm); }} />
                            <IconButton size="xs" icon={<FiTrash2 size={10} />} variant="ghost" colorScheme="red" aria-label="Delete"
                              onClick={async () => { await api.delete(`/contacts/${selected.id}/comms/${comm.id}`); setComms(c => c.filter(x => x.id !== comm.id)); }} />
                          </HStack>
                        </Flex>
                      )}
                    </Box>
                  ))}
                  {comms.length === 0 && !editingComm && <Text fontSize="11px" color="gray.400" fontStyle="italic">No additional details yet</Text>}
                </VStack>
              </Box>
              <Divider />

              <HStack w="100%" justify="flex-end">
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving}>Save</Button>
              </HStack>
            </VStack>
          )}

          {/* ── Add new contact form ── */}
          {addingContact && (
            <Box p={3} border="1px" borderColor="teal.200" borderRadius="md" bg="teal.50">
              <Text fontSize="13px" fontWeight="700" color="teal.700" mb={3}>New Contact</Text>
              <VStack spacing={3} align="stretch">
                {/* Company picker */}
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
                  <Box position="relative">
                    <Input size="sm" bg="white" placeholder="Search company..." value={companySearch}
                      onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); if (!e.target.value) setContactForm(p=>({...p,company_id:'',company_name:''})); }}
                      onBlur={() => setTimeout(() => setCompanySuggestions([]), 200)} />
                    {companySuggestions.length > 0 && (
                      <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
                        borderRadius="md" shadow="md" zIndex={50} maxH="140px" overflowY="auto">
                        {companySuggestions.map(co => (
                          <Box key={co.id} px={3} py={2} cursor="pointer" fontSize="12px" _hover={{bg:'teal.50'}}
                            onClick={() => { setContactForm(p=>({...p,company_id:co.id,company_name:co.name})); setCompanySearch(co.name); setCompanySuggestions([]); }}>
                            {co.name}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {contactForm.company_name && <Text fontSize="10px" color="teal.600" mt={0.5}>✓ {contactForm.company_name}</Text>}
                </FormControl>
                <HStack spacing={2}>
                  <FormControl isRequired flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>First Name *</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.first_name} onChange={e=>setContactForm(p=>({...p,first_name:e.target.value}))} autoFocus />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Last Name</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.last_name} onChange={e=>setContactForm(p=>({...p,last_name:e.target.value}))} />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Email</FormLabel>
                  <Input size="sm" bg="white" type="email" value={contactForm.email} onChange={e=>setContactForm(p=>({...p,email:e.target.value}))} />
                </FormControl>
                <HStack spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Mobile</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.mobile} onChange={e=>setContactForm(p=>({...p,mobile:e.target.value}))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Landline</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.landline} onChange={e=>setContactForm(p=>({...p,landline:e.target.value}))} />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Job Title</FormLabel>
                  <Input size="sm" bg="white" value={contactForm.job_title} onChange={e=>setContactForm(p=>({...p,job_title:e.target.value}))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Notes / Description</FormLabel>
                  <Textarea size="sm" bg="white" rows={2} value={contactForm.description} onChange={e=>setContactForm(p=>({...p,description:e.target.value}))} placeholder="Any notes about this contact..." />
                </FormControl>
                <HStack justify="flex-end" spacing={2}>
                  <Button size="sm" variant="ghost" onClick={() => setAddingContact(false)}>Cancel</Button>
                  <Button size="sm" colorScheme="teal" isLoading={savingContact} onClick={saveNewContact}>Save Contact</Button>
                </HStack>
              </VStack>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          {!addingContact && !editing && (
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="teal" variant="outline" mr="auto"
              onClick={() => { setAddingContact(true); setSelected(null); setContactForm({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'', company_id:'', company_name:'' }); setCompanySearch(''); setCompanySuggestions([]); }}>
              Add Contact
            </Button>
          )}
          {addingContact && <Button size="sm" variant="ghost" mr="auto" onClick={() => setAddingContact(false)}>Cancel</Button>}
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
